import { __decorate, __metadata } from "tslib";
import { EventEmitterWithErrorHandling } from '@fluidframework/telemetry-utils';
import { logActivity, logger } from '../logger';
import { ActivityEventType, MfsStatusCode } from '@metaos/mfs-telemetry';
import { getClientConfig } from '../mfs/mfsService';
import { areEqual } from '@metaos/mfs-client';
/**
 * Singleton provider of client configurations. When started, retrieves configurations periodically and raises a
 * clientConfigChanged event if any configuration has been changed.
 */
export class ClientConfigProvider extends EventEmitterWithErrorHandling {
    /**
     * ClientConfigProvider constructor
     *
     * @param config Configuration to initialize an instance of ClientConfigProvider
     */
    constructor(config) {
        super((_, unknownError) => {
            const error = unknownError instanceof Error ? unknownError : undefined;
            logger.logError('clientConfigChangeListenerError', MfsStatusCode.UnprocessableEntity, {
                message: `Error processing clientConfigChange listeners`,
                error,
            }, 'constructor');
        });
        this.config = config;
    }
    /** Initializes the singleton instance of ClientConfigProvider. */
    static initialize(config) {
        if (this.instance) {
            throw new Error('ClientConfigProvider already initialized');
        }
        this.instance = new ClientConfigProvider(config);
    }
    /** Disposes of any resources allocated during initialization */
    static dispose() {
        var _a;
        (_a = this.instance) === null || _a === void 0 ? void 0 : _a.stop();
        this.instance = undefined;
    }
    /** Gets a singleton instance of ClientConfigProvider. The instance must first be created by calling the initialize method. */
    static getInstance() {
        if (this.instance) {
            return this.instance;
        }
        throw new Error('ClientConfigProvider not initialized');
    }
    /** Starts the configuration provider and periodically retrieve configurations */
    start() {
        if (this.timeoutId) {
            throw new Error('ClientConfigProvider already started');
        }
        this.timeoutId = setInterval(() => {
            this.retrieveClientConfig().catch((unknownError) => {
                const error = unknownError instanceof Error ? unknownError : undefined;
                logger.logError('timedRetrieveClientConfigError', MfsStatusCode.UnprocessableEntity, {
                    message: 'Error attempting to retrieve client configuration',
                    error,
                }, 'start');
            });
        }, ClientConfigProvider.intervalMs);
    }
    /** Stops this configuration provider */
    stop() {
        clearInterval(this.timeoutId);
    }
    /**
     * Retrieves configurations from MFS Service. Emits a clientConfigChanged event if retrieving a configuration for the
     * first time, or the configuration changed from a previous retrieval.
     */
    async retrieveClientConfig() {
        const configResponse = await getClientConfig(this.config.getAccessTokenCallback, this.config.sessionId, this.config.mfsServiceDomain);
        if (!configResponse.success || !configResponse.clientConfig) {
            throw new Error('Could not retrieve client configuration');
        }
        const config = configResponse.clientConfig;
        if (!this.lastConfig || !areEqual(this.lastConfig, config)) {
            this.emit('clientConfigChanged', config);
        }
        this.lastConfig = config;
        return config;
    }
}
/** Time interval between each configuration retrieval attempt */
ClientConfigProvider.intervalMs = 30 * 60 * 1000; // 30 minutes
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ClientConfigProvider.prototype, "start", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ClientConfigProvider.prototype, "stop", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], ClientConfigProvider.prototype, "retrieveClientConfig", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], ClientConfigProvider, "initialize", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ClientConfigProvider, "dispose", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", ClientConfigProvider)
], ClientConfigProvider, "getInstance", null);
//# sourceMappingURL=clientConfigProvider.js.map